import { defineStore } from "pinia";
import { handleUserMessage } from "./handlers/user.handler";
import { handleOrgaMessage } from "./handlers/orga.handler";
import { handleOwnerMessage } from "./handlers/owner.handler";
import { handleManagerMessage } from "./handlers/manager.handler";
import { handleFamilyGroupMessage } from "./handlers/familyGroup.handler";
import { handleEntityMessage } from "./handlers/entity.handler";
import { channels, type EntityChannelType } from "./channels";
import type { TransmitSubscription, HandlerFunction } from "./types";
import type User from "../../../../backend/app/models/user";
import type { Transmit } from "@adonisjs/transmit-client";

const IDLE_HIDE_DELAY_MS = 10_000;
const REOPEN_DELAY_MS = 150;

export const useTransmitStore = defineStore("transmit", () => {
  // Transmit client instance - initialized once at store creation (not reactive!)
  let transmit: Transmit | null = null;
  if (import.meta.client) {
    try {
      const { $transmit } = useNuxtApp();
      transmit = markRaw($transmit as Transmit);
      // console.log("[Transmit] Client initialized");
    } catch (error) {
      console.warn("[Transmit] Failed to initialize client:", error);
    }
  }

  // Active subscriptions tracked by channel name
  // Each entry stores the subscription, the onMessage unsubscribe function,
  // and the in-flight create() promise so unsubscribe can wait for it.
  const handlers: Record<
    string,
    {
      subscription: TransmitSubscription;
      unsubscribe: (() => void) | null;
      createPromise: Promise<void> | null;
    }
  > = {};

  const isInitialized = ref(false);
  const isInitializing = ref(false);
  let initPromise: Promise<void> | null = null;

  // Last init args — used by the visibility handler to reconnect after
  // the tab becomes visible again, without dashboard help.
  const lastUser = ref<User | null>(null);
  const lastOrga = ref<{ id: number } | null>(null);
  const lastGroupId = ref<number | null>(null);

  const isAvailable = computed(() => transmit !== null);
  const isConnected = computed(() => Object.keys(handlers).length > 0);

  function subscribe(channel: string, handler: HandlerFunction) {
    if (!isAvailable.value) {
      // console.warn(`[Transmit] Not available for channel: ${channel}`);
      return;
    }

    // Skip if already subscribed
    if (handlers[channel]) {
      console.warn(`[Transmit] Already subscribed to: ${channel}`);
      return;
    }

    try {
      const subscription = transmit!.subscription(channel) as TransmitSubscription | null;
      if (!subscription) {
        console.error(`[Transmit] Failed to create subscription for: ${channel}`);
        return;
      }

      // onMessage returns an unsubscribe function - we MUST save it!
      const unsubscribe = subscription.onMessage((data: unknown) => {
        handler(data);
      });

      // Track the in-flight create() promise so unsubscribe can await it
      // before calling delete() (avoids races during cleanup).
      const createPromise = subscription.create().catch((error: unknown) => {
        console.error(`[Transmit] Error subscribing to ${channel}:`, error);
      });

      // Store both the subscription and its unsubscribe function
      handlers[channel] = {
        subscription: subscription,
        unsubscribe: typeof unsubscribe === "function" ? unsubscribe : null,
        createPromise,
      };
    } catch (error) {
      console.error(`[Transmit] Error subscribing to ${channel}:`, error);
    }
  }

  async function unsubscribe(channel: string) {
    const entry = handlers[channel];
    if (!entry) return;

    try {
      // First, remove the onMessage handler by calling the unsubscribe function
      if (entry.unsubscribe) {
        entry.unsubscribe();
        // console.log(`[Transmit] ✓ Removed message handler for: ${channel}`);
      }

      // Wait for any in-flight create() to settle before delete() so we
      // don't race a handshake with teardown.
      if (entry.createPromise) {
        try {
          await entry.createPromise;
        } catch {
          // Errors already logged in subscribe(); swallow here so cleanup
          // still attempts delete().
        }
      }

      // Then close the SSE connection
      await entry.subscription.delete();

      // Finally, remove from tracking
      Reflect.deleteProperty(handlers, channel);
      // console.log(`[Transmit] ✓ Unsubscribed from: ${channel}`);
    } catch (error) {
      console.error(`[Transmit] Error unsubscribing from ${channel}:`, error);
    }
  }

  function init(user: User, orga: { id: number } | null, groupId?: number | null) {
    if (import.meta.server) return;
    if (!user) return;
    if (isInitializing.value) return;

    // Already initialized. Callers that need to re-initialize (e.g. after a
    // group change) should call cleanup() first. This also avoids a deadlock
    // where init's work calls cleanup() while cleanup awaits initPromise.
    if (isInitialized.value) {
      console.warn(
        "[Transmit] init() called while already initialized. Call cleanup() first if you need to re-initialize.",
      );
      return;
    }

    // Remember the args so the visibility handler can reconnect after a
    // long hide without dashboard help.
    lastUser.value = user;
    lastOrga.value = orga;
    lastGroupId.value = groupId ?? null;

    // Fire-and-forget: we no longer await this. Callers (e.g. dashboard
    // layout) used to block navigation on SSE handshakes; now we kick
    // off subscriptions in parallel and return immediately.
    isInitializing.value = true;
    const work = (async () => {
      try {
        // console.log("[Transmit] Initializing subscriptions for user:", user.id);

        // User channel (always)
        subscribe(channels.user(user.id), handleUserMessage);

        if (orga) {
          subscribe(channels.orga(orga.id), handleOrgaMessage);

          // Role-based subscriptions
          if (user.role === "owner") {
            subscribe(channels.owner(user.id), handleOwnerMessage);
          }

          if (user.role === "manager" || user.role === "owner") {
            subscribe(channels.manager(orga.id), handleManagerMessage);
          }
        }

        // Family group subscription (if provided)
        if (groupId) {
          subscribe(channels.familyGroup(groupId), handleFamilyGroupMessage);
        }

        // Mark initialized as soon as the handshakes have been kicked
        // off — callers (and cleanup) shouldn't have to wait for the
        // server round-trips.
        isInitialized.value = true;
      } finally {
        isInitializing.value = false;
        initPromise = null;
      }
    })();

    initPromise = work;

    // Don't await in callers: surface the promise via initPromise for
    // cleanup, but never block.
    work.catch((error) => {
      console.error("[Transmit] Init failed:", error);
    });
  }

  function subscribeFamilyGroup(groupId: number) {
    if (!groupId) return;
    subscribe(channels.familyGroup(groupId), handleFamilyGroupMessage);
  }

  async function unsubscribeFamilyGroup(groupId: number) {
    if (!groupId) return;
    await unsubscribe(channels.familyGroup(groupId));
  }

  async function cleanup(clearLastInitArgs = false) {
    // Serialize cleanup with init so they don't race each other.
    isInitializing.value = true;
    try {
      // console.log("[Transmit] Cleaning up all subscriptions");
      // Wait for any in-flight init handshakes to finish before we tear
      // anything down, otherwise unsubscribe can race the create().
      if (initPromise) {
        try {
          await initPromise;
        } catch {
          // Errors already logged elsewhere.
        }
      }

      const channelNames = Object.keys(handlers);
      for (const channel of channelNames) {
        await unsubscribe(channel);
      }
      isInitialized.value = false;

      if (clearLastInitArgs) {
        lastUser.value = null;
        lastOrga.value = null;
        lastGroupId.value = null;
      }
    } finally {
      isInitializing.value = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Visibility-based pause/resume
  // ---------------------------------------------------------------------------
  // When the tab is hidden for a while we drop the SSE connection so the
  // browser isn't holding a useless socket open. On visible, we reconnect
  // with a small delay so navigation/data requests get the network slot
  // first.
  let visibilityTimeout: number | null = null;
  let visibilityReopenAttempts = 0;

  function clearVisibilityTimeout() {
    if (visibilityTimeout !== null) {
      clearTimeout(visibilityTimeout);
      visibilityTimeout = null;
    }
  }

  async function onVisibilityChange() {
    if (!import.meta.client) return;

    if (document.hidden) {
      visibilityReopenAttempts = 0;

      // Don't tear down immediately — only if the tab stays hidden long
      // enough that the user is actually away.
      clearVisibilityTimeout();
      visibilityTimeout = window.setTimeout(() => {
        visibilityTimeout = null;
        if (document.hidden) {
          // Pause the SSE connection; keep last init args so we can
          // resume on visible.
          void cleanup(false);
        }
      }, IDLE_HIDE_DELAY_MS);
    } else {
      // Visible again — cancel any pending pause and reconnect after a
      // small delay so navigation/data requests get priority.
      clearVisibilityTimeout();

      if (!isConnected.value && lastUser.value) {
        // If a cleanup is still in progress, wait a bit and recheck.
        // Cap the retries so we don't poll forever if something is stuck.
        if (isInitializing.value) {
          if (visibilityReopenAttempts < 6) {
            visibilityReopenAttempts++;
            window.setTimeout(() => {
              void onVisibilityChange();
            }, 500);
          } else {
            console.warn(
              "[Transmit] Giving up on visibility-driven reconnect while another init/cleanup is in progress. Will retry on the next visibility change.",
            );
          }
          return;
        }

        visibilityReopenAttempts = 0;
        const user = lastUser.value;
        const orga = lastOrga.value;
        const groupId = lastGroupId.value;
        window.setTimeout(() => {
          init(user, orga, groupId);
        }, REOPEN_DELAY_MS);
      }
    }
  }

  if (import.meta.client) {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }

  return {
    isConnected: readonly(isConnected),
    subscribe,
    unsubscribe,
    init,
    subscribeFamilyGroup,
    unsubscribeFamilyGroup,
    cleanup,

    // Entity-specific subscriptions for conflict detection
    subscribeEntity: (type: EntityChannelType, id: number) =>
      subscribe(channels.entity(type, id), handleEntityMessage),
    unsubscribeEntity: (type: EntityChannelType, id: number) =>
      unsubscribe(channels.entity(type, id)),
  };
});
